/**
 * ScanCenterDialog — browser-based scan tool. Storekeepers / Site Engineers
 * paste (or type, or scan-gun-into) a QR / RFID / MaterialUnit ID; the tool
 * resolves it to the unit, auto-suggests the next legal interface point,
 * and posts a ScanEvent (hash-chained) via `POST /mtx/scan`.
 *
 * Full PWA (camera + BT RFID + bulk + offline) ships in M6. This dialog
 * covers the desktop / plugged-in-scanner workflow today.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { X, ScanLine, Loader2, Check, AlertTriangle, MapPin, Package, Camera } from 'lucide-react';

const C = { primary: '#1E4E8C', accent: '#F26B1F', slate: '#0F172A',
             slateMuted: '#64748B', border: 'rgba(15,23,42,0.10)',
             success: '#10B981', warn: '#F59E0B', danger: '#EF4444' };

export default function ScanCenterDialog({ projectId, seedCode, onClose, onScanned }) {
  const [code, setCode] = useState(seedCode || '');
  const [unit, setUnit] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [interfacePoints, setInterfacePoints] = useState([]);
  const [suggested, setSuggested] = useState(null);
  const [selectedIp, setSelectedIp] = useState('');
  const [gps, setGps] = useState({ lat: '', lng: '' });
  const [notes, setNotes] = useState('');
  const [binId, setBinId] = useState('');
  const [override, setOverride] = useState(false);
  const [ovReason, setOvReason] = useState('');
  const [ovApprover, setOvApprover] = useState('');
  const [busy, setBusy] = useState(false);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const inputRef = useRef(null);

  // Auto-captured requester (JWT-stored profile) — surfaced in header +
  // sent to backend via existing `_actor()` bearer-token handshake.
  const user = (() => {
    try { return JSON.parse(localStorage.getItem('ciq_user') || '{}'); }
    catch { return {}; }
  })();

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Load all interface points once
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/mtx/interface-points`);
        setInterfacePoints(data.interfacePoints || []);
      } catch { /* silent */ }
    })();
  }, [projectId]);

  const doLookup = useCallback(async (q) => {
    if (!q || !q.trim()) return;
    setLookupBusy(true);
    setUnit(null); setCandidates([]); setSuggested(null); setSelectedIp('');
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/mtx/material-units/lookup?q=${encodeURIComponent(q.trim())}`);
      if (data.match) {
        setUnit(data.match);
        // Load suggested next interface and recent scan history
        const [sug, sc] = await Promise.all([
          api.get(`/v1/projects/${projectId}/mtx/material-units/${data.match.id}/next-interface`),
          api.get(`/v1/projects/${projectId}/mtx/scan-events?materialUnitId=${data.match.id}&limit=5`),
        ]);
        setSuggested(sug.data);
        setSelectedIp(sug.data.interfacePoint?.id || '');
        setRecent(sc.data.events || []);
      } else {
        setCandidates(data.candidates || []);
        // SWM fallback — QR JSON payloads and slashy mark numbers (like
        // '/PNOD-012') never live in the MaterialUnit index. Route them
        // through the SWM scan resolver so the storeroom still gets a hit.
        if ((data.candidates || []).length === 0) {
          try {
            const s = await api.get(`/v1/projects/${projectId}/swm/scan`, { params: { token: q.trim() } });
            if (s.data?.resolved) {
              const preset = s.data.mirPreset;
              toast.success(`SWM mark ${preset.sourceMark} → open MIR to issue`);
              // Synthesised MaterialUnit so the dialog can post a scan event
              // AND surface the MIR deep-link for one-click issue-flow.
              setUnit({
                id: `swm-${preset.sourceSerial}`,
                // Fields the resolved-unit card actually reads:
                materialCode:        preset.materialCode,
                materialDescription: preset.description || `Mark ${preset.sourceMark}`,
                mciLineNumber:       `SWM · ${preset.sourceMark}`,
                vendor:              (s.data.mark?.fabricator) || '—',
                currentState:        'SWM_DISPATCHED',
                // SWM-specific extras (used by MIR deep-link + toast):
                uom:          preset.uom,
                sourceModule: 'SWM',
                sourceMark:   preset.sourceMark,
                sourceSerial: preset.sourceSerial,
                weightKg:     s.data.mark?.weightKg,
                mirDeepLink:  (() => {
                  const p = new URLSearchParams();
                  p.set('openMir', '1');
                  p.set('prefillMark',      preset.sourceMark || '');
                  if (preset.materialCode)  p.set('prefillCode', preset.materialCode);
                  if (preset.description)   p.set('prefillDesc', preset.description);
                  if (preset.uom)           p.set('prefillUom',  preset.uom);
                  if (preset.qty)           p.set('prefillQty',  String(preset.qty));
                  if (preset.workfront)     p.set('prefillWorkfront',  preset.workfront);
                  if (preset.warehouseHint) p.set('prefillWarehouse',  preset.warehouseHint);
                  return `/project-controls/material-tracking?${p.toString()}`;
                })(),
              });
              return;
            }
          } catch { /* swallow — fall through to 'No match' */ }
          toast.error('No match');
        }
      }
    } catch (e) {
      toast.error('Lookup failed');
    }
    setLookupBusy(false);
  }, [projectId]);

  // Debounced auto-lookup as user types (waits 400ms after last keystroke)
  useEffect(() => {
    if (!code) return;
    const t = setTimeout(() => doLookup(code), 400);
    return () => clearTimeout(t);
  }, [code, doLookup]);

  const submitScan = async () => {
    if (!unit) { toast.error('Resolve a MaterialUnit first'); return; }
    if (!selectedIp) { toast.error('Pick an Interface Point'); return; }
    if (override && (!ovReason.trim() || !ovApprover.trim())) {
      toast.error('Override requires reason + approver'); return;
    }
    setBusy(true);
    try {
      const body = {
        materialUnitId: unit.id, interfacePointId: selectedIp,
        gpsLat: gps.lat ? parseFloat(gps.lat) : undefined,
        gpsLng: gps.lng ? parseFloat(gps.lng) : undefined,
        binId: binId || undefined, notes: notes || undefined,
        isOverride: override,
        overrideReason: override ? ovReason : undefined,
        overrideApprovedBy: override ? ovApprover : undefined,
      };
      const { data } = await api.post(`/v1/projects/${projectId}/mtx/scan`, body);
      toast.success(`Scanned → ${data.newState.replace(/_/g, ' ')} · hash ${String(data.event.hashCurrent).slice(0, 10)}…`);
      onScanned?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scan failed — check TTL / prerequisites');
    }
    setBusy(false);
  };

  const useGps = () => {
    if (!navigator.geolocation) return toast.error('Geolocation unavailable');
    navigator.geolocation.getCurrentPosition(
      p => setGps({ lat: p.coords.latitude.toFixed(6), lng: p.coords.longitude.toFixed(6) }),
      () => toast.error('GPS permission denied'));
  };

  const grouped = interfacePoints.reduce((acc, ip) => {
    if (!acc[ip.type]) acc[ip.type] = [];
    acc[ip.type].push(ip); return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}
      data-testid="scan-center-dialog">
      <div className="bg-white rounded-xl w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-3 border-b flex items-center justify-between sticky top-0 bg-white z-10" style={{ borderColor: C.border }}>
          <div className="flex items-center gap-2">
            <ScanLine className="w-5 h-5" style={{ color: C.accent }} />
            <div>
              <div className="text-sm font-bold" style={{ color: C.slate }}>Scan Center</div>
              <div className="text-[10px]" style={{ color: C.slateMuted }}>
                Scanned by <b>{user.name || user.email || 'current user'}</b> · {new Date().toLocaleString()} · hash-chained + TTL enforced
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="scan-center-close"><X className="w-4 h-4" style={{ color: C.slateMuted }} /></button>
        </div>

        <div className="p-4 space-y-3">
          {/* Code input */}
          <div>
            <label className="text-[10px] uppercase font-semibold mb-1 block" style={{ color: C.slateMuted }}>
              Scan Input
            </label>
            <div className="flex gap-1.5">
              <input ref={inputRef} value={code} onChange={e => setCode(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && doLookup(code)}
                placeholder="e.g. QR-MU-FA32C813  ·  RFID-3B2A9F  ·  MU-FA32C813"
                className="flex-1 px-3 py-2 rounded border font-mono text-xs"
                style={{ borderColor: C.border }}
                data-testid="scan-code-input" />
              <label className="text-white px-2.5 py-2 rounded font-semibold cursor-pointer flex items-center gap-1 text-xs"
                style={{ background: '#F26B1F' }} data-testid="scan-camera-btn"
                title="Snap a barcode/QR using the device camera">
                📷
                <input type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={async e => {
                    const f = e.target.files?.[0]; if (!f) return;
                    // On mobile this fires the camera. On desktop this is a
                    // file picker. Either way, once the image lands we try to
                    // read a QR from it via BarcodeDetector when available.
                    try {
                      if ('BarcodeDetector' in window) {
                        const bd = new window.BarcodeDetector({ formats: ['qr_code', 'code_128'] });
                        const bmp = await createImageBitmap(f);
                        const codes = await bd.detect(bmp);
                        if (codes && codes.length) {
                          setCode(codes[0].rawValue);
                          doLookup(codes[0].rawValue);
                          toast.success('QR detected: ' + codes[0].rawValue.slice(0, 24));
                          return;
                        }
                      }
                      toast.info('Photo captured. Paste the code manually if the auto-decode did not work.');
                    } catch (err) { toast.error('Could not decode: ' + err.message); }
                  }} />
              </label>
              <Button size="sm" onClick={() => doLookup(code)} disabled={lookupBusy}
                data-testid="scan-lookup-btn"
                className="text-white" style={{ background: C.primary }}>
                {lookupBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Resolve'}
              </Button>
            </div>
            <div className="text-[9px] mt-0.5" style={{ color: C.slateMuted }}>
              Tip: HID barcode / RFID readers appear as keyboard input — this field auto-resolves 400ms after your last keystroke.
            </div>
          </div>

          {/* Resolved unit */}
          {unit && (
            <div className="p-2 rounded border" style={{ borderColor: C.success, background: '#F0FDF4' }}
              data-testid="scan-resolved-unit">
              <div className="flex items-center gap-1.5 mb-1">
                <Check className="w-3.5 h-3.5" style={{ color: C.success }} />
                <div className="text-[10px] uppercase font-bold" style={{ color: C.success }}>Matched</div>
                <span className="ml-auto text-[9px] font-mono" style={{ color: C.slateMuted }}>{unit.id}</span>
              </div>
              <div className="text-[11px] font-bold" style={{ color: C.slate }}>{unit.materialDescription}</div>
              <div className="text-[9px] mt-0.5" style={{ color: C.slateMuted }}>
                MCI {unit.mciLineNumber} · Vendor {unit.vendor} · State{' '}
                <span className="px-1 rounded text-white text-[9px]" style={{ background: C.primary }}>{unit.currentState}</span>
              </div>
              {suggested?.suggestedType && (
                <div className="mt-1.5 text-[10px] p-1.5 rounded" style={{ background: '#FEF3C7', color: '#92400E' }}>
                  <span className="font-semibold">Suggested next scan: </span>{suggested.suggestedType.replace(/_/g, ' ')}
                </div>
              )}
            </div>
          )}

          {candidates.length > 0 && (
            <div className="p-2 rounded border" style={{ borderColor: C.warn, background: '#FEF3C7' }}>
              <div className="text-[10px] mb-1" style={{ color: '#92400E' }}>Multiple candidates — pick one:</div>
              {candidates.slice(0, 8).map(c => (
                <button key={c.id} onClick={() => { setCode(c.id); setUnit(c); setCandidates([]); doLookup(c.id); }}
                  className="w-full text-left p-1.5 mb-0.5 text-[10px] rounded hover:bg-white"
                  data-testid={`scan-candidate-${c.id}`}>
                  <span className="font-mono font-bold" style={{ color: C.primary }}>{c.id}</span>
                  <span className="ml-1" style={{ color: C.slate }}>· {c.mciLineNumber} · {c.materialDescription?.slice(0, 40)}</span>
                </button>
              ))}
            </div>
          )}

          {/* Interface picker */}
          {unit && (
            <div>
              <label className="text-[10px] uppercase font-semibold mb-1 block" style={{ color: C.slateMuted }}>
                Interface Point *
              </label>
              <select value={selectedIp} onChange={e => setSelectedIp(e.target.value)}
                className="w-full px-2 py-2 rounded border text-xs"
                style={{ borderColor: C.border }}
                data-testid="scan-ip-select">
                <option value="">— select —</option>
                {Object.entries(grouped).map(([type, ips]) => (
                  <optgroup key={type} label={type.replace(/_/g, ' ')}>
                    {ips.map(ip => (
                      <option key={ip.id} value={ip.id}>#{type} · {ip.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          )}

          {/* Optional GPS + Bin + Notes */}
          {unit && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase font-semibold mb-1 block" style={{ color: C.slateMuted }}>GPS Lat</label>
                <input value={gps.lat} onChange={e => setGps({ ...gps, lat: e.target.value })}
                  className="w-full px-2 py-1 rounded border text-xs" style={{ borderColor: C.border }}
                  data-testid="scan-gps-lat" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-semibold mb-1 block" style={{ color: C.slateMuted }}>GPS Lng</label>
                <div className="flex gap-1">
                  <input value={gps.lng} onChange={e => setGps({ ...gps, lng: e.target.value })}
                    className="flex-1 px-2 py-1 rounded border text-xs" style={{ borderColor: C.border }}
                    data-testid="scan-gps-lng" />
                  <button onClick={useGps} title="Use device GPS"
                    className="px-1.5 rounded border text-[10px]" style={{ borderColor: C.border, color: C.primary }}>
                    <MapPin className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase font-semibold mb-1 block" style={{ color: C.slateMuted }}>Bin ID (only for BIN_PUTAWAY)</label>
                <input value={binId} onChange={e => setBinId(e.target.value)}
                  placeholder="BIN-A3-14" className="w-full px-2 py-1 rounded border text-xs"
                  style={{ borderColor: C.border }} data-testid="scan-bin-id" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase font-semibold mb-1 block" style={{ color: C.slateMuted }}>Notes</label>
                <input value={notes} onChange={e => setNotes(e.target.value)}
                  className="w-full px-2 py-1 rounded border text-xs" style={{ borderColor: C.border }}
                  data-testid="scan-notes" />
              </div>
            </div>
          )}

          {/* Override */}
          {unit && (
            <div className="p-2 rounded border" style={{ borderColor: override ? C.danger : C.border, background: override ? '#FEE2E2' : '#FAFAFA' }}>
              <label className="flex items-center gap-1.5 text-[10px] font-semibold cursor-pointer" style={{ color: C.slate }}>
                <input type="checkbox" checked={override} onChange={e => setOverride(e.target.checked)}
                  data-testid="scan-override-toggle" />
                <AlertTriangle className="w-3.5 h-3.5" style={{ color: C.danger }} />
                Supervisor Override (bypasses prerequisite state + TTL) — logged as red-flag
              </label>
              {override && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <input value={ovReason} onChange={e => setOvReason(e.target.value)}
                    placeholder="Reason *" className="px-2 py-1 rounded border text-xs"
                    style={{ borderColor: C.border }} data-testid="scan-override-reason" />
                  <input value={ovApprover} onChange={e => setOvApprover(e.target.value)}
                    placeholder="Approver email *" className="px-2 py-1 rounded border text-xs"
                    style={{ borderColor: C.border }} data-testid="scan-override-approver" />
                </div>
              )}
            </div>
          )}

          {/* Recent scans */}
          {unit && recent.length > 0 && (
            <div className="p-2 rounded border" style={{ borderColor: C.border, background: '#FAFAFA' }}>
              <div className="text-[9px] uppercase font-bold mb-1" style={{ color: C.slateMuted }}>Recent scans on this unit</div>
              {recent.map((r, i) => (
                <div key={i} className="text-[9.5px] font-mono flex items-center gap-1.5" style={{ color: C.slate }}>
                  <Check className="w-2.5 h-2.5" style={{ color: C.success }} />
                  <span>{r.interfacePointType}</span>
                  <span style={{ color: C.slateMuted }}>· {(r.scannedAt || '').slice(11, 19)}</span>
                  <span style={{ color: C.slateMuted }}>by {r.scannedBy}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-3 border-t flex justify-end gap-2 sticky bottom-0 bg-white" style={{ borderColor: C.border }}>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={submitScan} disabled={busy || !unit || !selectedIp}
            className="text-white font-semibold" style={{ background: C.accent }}
            data-testid="scan-submit-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ScanLine className="w-3.5 h-3.5 mr-1" />}
            Post Scan
          </Button>
        </div>
      </div>
    </div>
  );
}
